import { memo } from "react";

import { useTranslation } from 'react-i18next';
const shimmer = `relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_2s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transposable`;

function SkeletonBlock({ className = "" }) {
    const { t } = useTranslation();
return (
    <div className={`bg-zinc-800/60 rounded ${shimmer} ${className}`} />
  );
}

export const PageSkeleton = memo(function PageSkeleton() {
  return (
    <div className="min-h-screen bg-[#09090B] animate-pulse">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
        {/* Title area */}
        <SkeletonBlock className="h-4 w-32 mb-3" />
        <SkeletonBlock className="h-8 sm:h-10 w-64 sm:w-96 mb-4" />
        <SkeletonBlock className="h-4 w-full max-w-xl mb-8" />
        {/* Content grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="border border-zinc-800 bg-zinc-950/30 p-4 sm:p-6">
              <SkeletonBlock className="h-6 w-3/4 mb-3" />
              <SkeletonBlock className="h-4 w-full mb-2" />
              <SkeletonBlock className="h-4 w-2/3 mb-4" />
              <SkeletonBlock className="h-8 w-full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

export const CardSkeleton = memo(function CardSkeleton({ count = 3 }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="border border-zinc-800 bg-zinc-950/30 p-4 sm:p-6 animate-pulse">
          <SkeletonBlock className="h-6 w-3/4 mb-3" />
          <SkeletonBlock className="h-4 w-full mb-2" />
          <SkeletonBlock className="h-4 w-2/3" />
        </div>
      ))}
    </div>
  );
});

export const TableSkeleton = memo(function TableSkeleton({ rows = 5 }) {
  return (
    <div className="border border-zinc-800 bg-zinc-950/30 animate-pulse">
      <div className="px-4 py-3 border-b border-zinc-800 flex gap-4">
        <SkeletonBlock className="h-4 w-24" />
        <SkeletonBlock className="h-4 w-32" />
        <SkeletonBlock className="h-4 w-20" />
        <SkeletonBlock className="h-4 w-16" />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="px-4 py-3 border-b border-zinc-800/50 flex gap-4">
          <SkeletonBlock className="h-4 w-24" />
          <SkeletonBlock className="h-4 w-32" />
          <SkeletonBlock className="h-4 w-20" />
          <SkeletonBlock className="h-4 w-16" />
        </div>
      ))}
    </div>
  );
});

export const ChartSkeleton = memo(function ChartSkeleton() {
  return (
    <div className="border border-zinc-800 bg-zinc-950/30 p-4 sm:p-6 animate-pulse">
      <div className="flex items-center justify-between mb-4">
        <SkeletonBlock className="h-5 w-32" />
        <SkeletonBlock className="h-8 w-24" />
      </div>
      <div className="flex items-end gap-1 sm:gap-2 h-32 sm:h-48">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 bg-zinc-800/60 rounded-t"
            style={{ height: `${20 + Math.random() * 80}%` }}
          />
        ))}
      </div>
    </div>
  );
});

export const DashboardSkeleton = memo(function DashboardSkeleton() {
  return (
    <div className="min-h-screen bg-[#09090B] animate-pulse">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-8">
        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="border border-zinc-800 bg-zinc-950/30 p-3 sm:p-4">
              <SkeletonBlock className="h-3 w-16 mb-2" />
              <SkeletonBlock className="h-6 sm:h-8 w-20" />
            </div>
          ))}
        </div>
        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
          <ChartSkeleton />
          <ChartSkeleton />
        </div>
        {/* Table */}
        <TableSkeleton rows={4} />
      </div>
    </div>
  );
});

export default PageSkeleton;
